import { GameConfig } from "@/config/GameConfig";
import type { Point } from "@/core/types";
import type { LevelDefinition } from "./types";
import { horizontalWall, mergeUnique, rect, subtract, verticalWall } from "./geometry";

// Same 18x18 board as world1/world2 - MARS raises the difficulty through
// obstacle geometry, route commitment, and target length, never through a
// world-specific speed value or a new mechanic. See
// `GameConfig.gameplay.worldBaseStepMs.w3` for this world's base speed.
const GRID = { cols: 18, rows: 18 };

/** Removes duplicate cells from a border/perimeter outline. */
function rectBorder(xFrom: number, yFrom: number, xTo: number, yTo: number): Point[] {
  return mergeUnique(horizontalWall(yFrom, xFrom, xTo), horizontalWall(yTo, xFrom, xTo), verticalWall(xFrom, yFrom, yTo), verticalWall(xTo, yFrom, yTo));
}

/**
 * LANDING SITE's crater rim: a single, weathered impact crater with its
 * rim broken open toward the player's approach (south-west) and two small
 * ejecta/boulder clusters nearby for texture. The rim is hand-authored
 * (not a clean radius formula) so it reads as a natural, unevenly-broken
 * crater rather than a perfect circle - the exact cell list below is the
 * locked, approved shape from the Mars design pass, not a placeholder.
 */
const CRATER_RIM: Point[] = [
  { x: 7, y: 5 }, { x: 8, y: 5 }, { x: 9, y: 5 }, { x: 10, y: 5 }, { x: 11, y: 5 },
  { x: 6, y: 6 }, { x: 7, y: 6 }, { x: 11, y: 6 }, { x: 12, y: 6 },
  { x: 5, y: 7 }, { x: 6, y: 7 }, { x: 12, y: 7 }, { x: 13, y: 7 },
  { x: 5, y: 8 }, { x: 13, y: 8 },
  { x: 5, y: 9 }, { x: 13, y: 9 },
  { x: 5, y: 10 }, { x: 13, y: 10 },
  { x: 5, y: 11 }, { x: 12, y: 11 }, { x: 13, y: 11 },
  { x: 11, y: 12 }, { x: 12, y: 12 },
  { x: 10, y: 13 }, { x: 11, y: 13 },
];

/**
 * THE CANYON's diagonal wall: a wide (3-cell) canyon face sweeping from
 * the top-left toward the bottom-right, kept clear of every board edge
 * (rows 0-1 and 16-17, and never reaching columns 0/17) so a WORMHOLE wrap
 * can never land inside it - see `wormhole.mapSafety.test.ts`. `gapRows`
 * are left completely open as the level's two crossings.
 */
function canyonWall(gapRows: ReadonlySet<number>): Point[] {
  const points: Point[] = [];
  for (let y = 2; y <= 15; y++) {
    if (gapRows.has(y)) continue;
    const xStart = 1 + Math.floor((y - 2) * 0.85);
    points.push({ x: xStart, y }, { x: xStart + 1, y }, { x: xStart + 2, y });
  }
  return points;
}

/**
 * TWIN CRATERS: the board is split into a west half (start) and an east
 * half (exit) connected ONLY by two crater crossings - there is no third,
 * neutral route around either one. The north crater (`x=5-13`, `y=0-7`)
 * is compact with a single-cell door on each side and an interior baffle
 * forcing a narrow S-detour: shorter, riskier. The south crater
 * (`x=3-15`, `y=8-17`) is wider with 2-cell doors and a fully open
 * interior: longer, safer. Both are open-mouth (anchored to the board's
 * own top/bottom edge) rather than fully enclosed, matching the same
 * pattern already used by `w2-l5` THE CORE's rings. `(5,17)`/`(13,17)`
 * mirror the top-edge cells `(5,0)`/`(13,0)` so a WORMHOLE vertical wrap
 * can never land on an obstacle - see the WORMHOLE map-safety invariant
 * test. These two cells are load-bearing, not decoration.
 */
const northCraterLeft = verticalWall(5, 0, 7);
const northCraterRight = verticalWall(13, 0, 7);
const northCraterBottom = horizontalWall(7, 6, 12);
const northCraterBaffle = verticalWall(9, 4, 6);
const northCraterDoors: Point[] = [{ x: 5, y: 6 }, { x: 13, y: 6 }];

const southCraterLeft = verticalWall(3, 8, 17);
const southCraterRight = verticalWall(15, 8, 17);
const southCraterTop = horizontalWall(8, 3, 15);
const southCraterDoors: Point[] = [{ x: 3, y: 16 }, { x: 3, y: 17 }, { x: 15, y: 16 }, { x: 15, y: 17 }];

const WORMHOLE_EDGE_MIRROR: Point[] = [{ x: 5, y: 17 }, { x: 13, y: 17 }];

/** One loop of TRENCH NETWORK's mesh: a rectangular ring with exactly two openings, so it is always a through-route, never a single-entry sack. */
function loopRing(x0: number, y0: number, x1: number, y1: number, gaps: Point[]): Point[] {
  return subtract(rectBorder(x0, y0, x1, y1), gaps);
}

/** One OUTPOST ALPHA station module: a rectangular room with two doors on different walls (a wide plaza-facing door and a narrower perimeter-facing junction), so every module is a through-route a long snake can cross, never a room it must reverse out of. */
function stationModule(x0: number, y0: number, x1: number, y1: number, doors: Point[]): Point[] {
  return subtract(rectBorder(x0, y0, x1, y1), doors);
}

export const WORLD_3_LEVELS: LevelDefinition[] = [
  {
    id: "w3-l1",
    name: "LANDING SITE",
    worldId: "w3",
    levelInWorld: 1,
    order: 11,
    grid: GRID,
    snakeStart: { x: 2, y: 2 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 29,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w3,
    // A broken crater rim with a wide, generous opening, plus two small
    // boulder clusters for texture - Mars's introduction: curved-space
    // navigation, no true dead end, no route commitment yet.
    obstacles: mergeUnique(CRATER_RIM, rect(2, 13, 3, 14), rect(14, 3, 15, 4)),
    exit: { x: 15, y: 15 },
    pace: { paceParMs: 156000, pacePointsPerSecond: 2, maxPaceBonus: 78 },
  },
  {
    id: "w3-l2",
    name: "THE CANYON",
    worldId: "w3",
    levelInWorld: 2,
    order: 12,
    grid: GRID,
    snakeStart: { x: 0, y: 3 },
    startDirection: "down",
    initialLength: 3,
    targetLength: 31,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w3,
    // A wide diagonal canyon wall with two crossings, plus a shallow
    // C-shaped cove (2-wide mouth) - a real tempting pocket that stays
    // safely escapable at any length, never a true dead end.
    obstacles: mergeUnique(canyonWall(new Set([5, 6, 12, 13])), rect(14, 7, 16, 7), rect(16, 8, 16, 9), rect(14, 10, 16, 10)),
    exit: { x: 16, y: 16 },
    pace: { paceParMs: 160000, pacePointsPerSecond: 2, maxPaceBonus: 84 },
  },
  {
    id: "w3-l3",
    name: "TWIN CRATERS",
    worldId: "w3",
    levelInWorld: 3,
    order: 13,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    // Gameplay-QA fix: with the locked starting body (head (2,9), body
    // extending west through (1,9)/(0,9)) and `startDirection: "right"`,
    // the very first automatic step would move the head onto (3,9) -
    // the south crater's own left wall - killing the run before the
    // player can act. "up" is legal ((2,8) is free open west-margin
    // ground) and keeps the player in the same shared west margin the
    // route choice is built around. Note this necessarily changes the
    // snake's ACTUAL starting body too: this engine always derives the
    // body from `snakeStart`/`startDirection`/`initialLength`
    // (`buildInitialSnakeBody` - there is no independently-stored body
    // field), so the real starting body is now (2,9)/(2,10)/(2,11), not
    // (1,9)/(0,9) - both are free, unobstructed cells in the same west
    // margin, so this is still a fully valid, in-bounds start.
    startDirection: "up",
    initialLength: 3,
    targetLength: 33,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w3,
    // Two genuinely different routes, not a third neutral bypass: a
    // short/narrow/risky crossing through the north crater, or a
    // longer/wider/safer crossing through the south crater. See this
    // file's crater doc comment above for the WORMHOLE-safety mirror.
    obstacles: mergeUnique(
      subtract(
        mergeUnique(northCraterLeft, northCraterRight, northCraterBottom, northCraterBaffle, southCraterLeft, southCraterRight, southCraterTop),
        [...northCraterDoors, ...southCraterDoors],
      ),
      WORMHOLE_EDGE_MIRROR,
    ),
    exit: { x: 16, y: 9 },
    pace: { paceParMs: 172000, pacePointsPerSecond: 2, maxPaceBonus: 90 },
  },
  {
    id: "w3-l4",
    name: "TRENCH NETWORK",
    worldId: "w3",
    levelInWorld: 4,
    order: 14,
    grid: GRID,
    snakeStart: { x: 2, y: 8 },
    startDirection: "right",
    initialLength: 3,
    // Gameplay QA (post-launch balance pass): 35 -> 33. Geometry
    // unchanged - this level's difficulty is meant to come from the
    // trench network's circulation pressure, not from forcing an
    // unnecessarily long snake. Deliberately equal to TWIN CRATERS'
    // target (33): TRENCH NETWORK is still harder in practice because
    // of its geometry/route-planning demands, not because of a longer
    // required body.
    targetLength: 33,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w3,
    // Three irregularly-sized loops (never a repeating grid) plus one
    // connecting through-spur, forming one interconnected mesh - advanced
    // multi-route circulation, zero true dead ends.
    obstacles: mergeUnique(
      loopRing(2, 2, 8, 5, [{ x: 2, y: 3 }, { x: 5, y: 5 }]),
      loopRing(11, 3, 15, 9, [{ x: 11, y: 5 }, { x: 13, y: 9 }]),
      loopRing(3, 11, 9, 15, [{ x: 6, y: 11 }, { x: 9, y: 13 }]),
      horizontalWall(15, 10, 12),
    ),
    exit: { x: 16, y: 16 },
    // Pace baseline scaled down proportionally with the reduced net
    // growth (32 -> 30 segments), keeping this level's own previously-set
    // seconds-per-segment rate (~5.8s) rather than inventing a new one.
    pace: { paceParMs: 174000, pacePointsPerSecond: 2, maxPaceBonus: 90 },
  },
  {
    id: "w3-l5",
    name: "OUTPOST ALPHA",
    worldId: "w3",
    levelInWorld: 5,
    order: 15,
    grid: GRID,
    snakeStart: { x: 5, y: 4 },
    startDirection: "right",
    initialLength: 3,
    // Gameplay QA (post-launch balance pass): 38 -> 36. Station geometry
    // unchanged - the finale should stay hard primarily because of its
    // dense routing, not because of excessive required body length.
    targetLength: 36,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w3,
    // Four research-station modules ringing a boulder-studded central
    // plaza. Every module has two doors on different walls (a wide
    // plaza-facing door, a narrow perimeter-facing junction), so the
    // finale is built from through-routes a long snake can cross, never
    // single-door rooms it must reverse out of.
    obstacles: mergeUnique(
      stationModule(2, 2, 7, 7, [{ x: 7, y: 4 }, { x: 7, y: 5 }, { x: 4, y: 7 }]),
      stationModule(10, 2, 15, 7, [{ x: 10, y: 4 }, { x: 10, y: 5 }, { x: 13, y: 7 }]),
      stationModule(2, 10, 7, 15, [{ x: 7, y: 12 }, { x: 7, y: 13 }, { x: 4, y: 10 }]),
      stationModule(10, 10, 15, 15, [{ x: 10, y: 12 }, { x: 10, y: 13 }, { x: 13, y: 10 }]),
      [{ x: 9, y: 8 }, { x: 8, y: 9 }],
    ),
    exit: { x: 9, y: 9 },
    // Pace baseline scaled down proportionally with the reduced net
    // growth (35 -> 33 segments), keeping this level's own previously-set
    // seconds-per-segment rate (~6.0s) rather than inventing a new one.
    pace: { paceParMs: 198000, pacePointsPerSecond: 2, maxPaceBonus: 99 },
  },
];
